import React, { useEffect, useMemo, useState } from 'react';
import { FaPlus, FaPen, FaTrash, FaChevronDown, FaSearch } from 'react-icons/fa';
import { toast } from 'react-toastify';
import FeatureFlagService, { ENVIRONMENTS } from '../../services/FeatureFlagService';
import DeleteSweetAlert from '../../utils/DeleteSweetAlert';
import FeatureFlagModal from './components/FeatureFlagModal';
import '../../assets/css/FeatureFlags.css';

// Coloured status dot per environment (matches design mock).
const ENV_DOT = {
  Local: '#64748b',
  Dev: '#2355e0',
  Prod: '#e0414e',
};

const prettyJson = (value) => {
  try {
    const obj = typeof value === 'string' ? JSON.parse(value || '{}') : (value ?? {});
    return JSON.stringify(obj, null, 2);
  } catch {
    return typeof value === 'string' ? value : '{}';
  }
};

const FeatureFlags = () => {
  const [rows, setRows] = useState([]); // one row per flag per environment
  const [loading, setLoading] = useState(true);
  const [activeEnv, setActiveEnv] = useState(ENVIRONMENTS[0]);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [modal, setModal] = useState({ show: false, mode: 'create', flag: null });

  const loadFlags = async () => {
    setLoading(true);
    try {
      const data = await FeatureFlagService.getAdminFlags();
      setRows(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load feature flags.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFlags();
  }, []);

  const enabledCount = (env) => rows.filter((r) => r.environment === env && r.isEnabled).length;
  const totalCount = (env) => rows.filter((r) => r.environment === env).length;

  const envRows = useMemo(
    () => rows.filter((r) => r.environment === activeEnv),
    [rows, activeEnv]
  );

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return envRows;
    return envRows.filter(
      (r) => r.name.toLowerCase().includes(q) || (r.description || '').toLowerCase().includes(q)
    );
  }, [envRows, search]);

  const handleToggle = async (row) => {
    try {
      await FeatureFlagService.toggleFlag(row.id, !row.isEnabled);
      // Optimistic-ish: update just this row locally, then refresh counts.
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, isEnabled: !row.isEnabled } : r)));
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update flag.');
    }
  };

  const handleSave = async (payload) => {
    try {
      if (modal.mode === 'edit') {
        // Edit applies only to the active environment's row.
        await FeatureFlagService.updateFlag({
          id: modal.flag.id,
          businessAdminIds: modal.flag.businessAdminIds,
          premiumFeature: modal.flag.premiumFeature,
          ...payload,
        });
        toast.success('Feature flag updated.');
      } else {
        await FeatureFlagService.createFlag(payload);
        toast.success('Feature flag created in all environments.');
      }
      setModal({ show: false, mode: 'create', flag: null });
      loadFlags();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save flag.');
    }
  };

  const handleDelete = async (row) => {
    const confirmed = await DeleteSweetAlert({
      title: 'Delete Feature Flag?',
      text: `Delete "${row.name}" from ${row.environment}? Other environments are not affected.`,
    });
    if (!confirmed) return;
    try {
      await FeatureFlagService.deleteFlag(row.id);
      if (expandedId === row.id) setExpandedId(null);
      loadFlags();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to delete flag.');
    }
  };

  const openCreate = () => setModal({ show: true, mode: 'create', flag: null });
  const openEdit = (row) => setModal({ show: true, mode: 'edit', flag: row });
  const closeModal = () => setModal((m) => ({ ...m, show: false }));

  return (
    <div className="ff-page">
      {/* Header */}
      <div className="ff-header">
        <div>
          <h1 className="ff-title">Feature Flags</h1>
          <p className="ff-subtitle">Create once — available in all environments, Off by default.</p>
        </div>
        <button type="button" className="ff-btn ff-btn-primary ff-create-btn" onClick={openCreate}>
          <FaPlus className="me-2" /> Create Feature
        </button>
      </div>

      {/* Environment tabs */}
      <div className="ff-tabs">
        {ENVIRONMENTS.map((env) => (
          <button
            key={env}
            type="button"
            className={`ff-tab ${activeEnv === env ? 'active' : ''}`}
            onClick={() => setActiveEnv(env)}
          >
            <span className="ff-tab-dot" style={{ backgroundColor: ENV_DOT[env] }} />
            {env}
            <span className="ff-tab-count">{enabledCount(env)}/{totalCount(env)}</span>
          </button>
        ))}
      </div>

      {/* Search + summary */}
      <div className="ff-controls">
        <div className="ff-search">
          <FaSearch className="ff-search-icon" />
          <input
            type="text"
            placeholder="Search feature flags..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <span className="ff-summary">
          {enabledCount(activeEnv)} of {totalCount(activeEnv)} enabled
        </span>
      </div>

      {/* Table */}
      <div className="ff-table">
        <div className="ff-table-head">
          <span className="ff-col-feature">FEATURE / DESCRIPTION</span>
          <span className="ff-col-attr">ATTRIBUTES</span>
        </div>

        {loading && <div className="ff-empty">Loading…</div>}

        {!loading && filteredRows.length === 0 && (
          <div className="ff-empty">No feature flags found.</div>
        )}

        {!loading && filteredRows.map((row) => {
          const on = !!row.isEnabled;
          const expanded = expandedId === row.id;
          return (
            <div key={row.id} className="ff-row-wrap">
              <div className="ff-row">
                <button
                  type="button"
                  className={`ff-toggle ${on ? 'on' : ''}`}
                  onClick={() => handleToggle(row)}
                  aria-label={`Toggle ${row.name} for ${activeEnv}`}
                >
                  <span className="ff-toggle-knob" />
                </button>

                <div className="ff-feature">
                  <div className="ff-feature-top">
                    <span className="ff-flag-name ff-mono">{row.name}</span>
                    <span className={`ff-badge ${on ? 'on' : ''}`}>{on ? 'On' : 'Off'}</span>
                    {row.premiumFeature && <span className="ff-badge ff-badge-premium">Premium</span>}
                  </div>
                  <div className="ff-feature-desc">{row.description}</div>
                </div>

                <button
                  type="button"
                  className={`ff-attr-btn ${expanded ? 'open' : ''}`}
                  onClick={() => setExpandedId(expanded ? null : row.id)}
                >
                  Attributes <FaChevronDown className="ff-attr-caret" />
                </button>

                <button type="button" className="ff-icon-btn" onClick={() => openEdit(row)} aria-label="Edit">
                  <FaPen />
                </button>
                <button type="button" className="ff-icon-btn" onClick={() => handleDelete(row)} aria-label="Delete">
                  <FaTrash />
                </button>
              </div>

              {expanded && (
                <div className="ff-attr-detail">
                  <pre className="ff-mono">{prettyJson(row.attributes)}</pre>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <FeatureFlagModal
        show={modal.show}
        mode={modal.mode}
        flag={modal.flag}
        onSave={handleSave}
        onClose={closeModal}
      />
    </div>
  );
};

export default FeatureFlags;
